import { ChangeDetectionStrategy, Component, computed, inject, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { EmpresaService, TenantService } from '@hta/auth';
import { SucursalContextService } from 'app/core/sucursal/sucursal-context.service';

/**
 * Badge reutilizable que muestra el Consorcio / Empresa / Sucursal activos.
 * Lee de las señales globales (SSOT) y se actualiza reactivamente:
 *  - tenantService.currentTenant()  → nombre del corporativo (se actualiza al renombrar en /corporativo)
 *  - empresaService.currentEmpresa()
 *  - sucursalContextService.currentSucursal()
 *
 * Variantes:
 *  - variant="full"   → bloques apilados con icono (por defecto, para el footer del sidebar)
 *  - variant="inline"  → texto compacto en una línea (para el topbar/header)
 */
@Component({
    selector: 'app-tenant-badge',
    templateUrl: './tenant-badge.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [],
})
export class TenantBadgeComponent implements OnInit {
    @Input() variant: 'full' | 'inline' = 'full';

    private readonly tenantService  = inject(TenantService);
    private readonly empresaService = inject(EmpresaService);
    private readonly sucursalContext = inject(SucursalContextService);

    readonly currentTenant = this.tenantService.currentTenant;
    readonly currentEmpresa = this.empresaService.currentEmpresa;
    readonly currentSucursal = this.sucursalContext.currentSucursal;

    async ngOnInit(): Promise<void> {
        // Asegurar que exista un contribuyente "actual" para mostrarlo
        // (la primera vez el EmpresaService arranca sin currentEmpresa).
        if (!this.empresaService.currentEmpresa()) {
            try {
                await this.empresaService.loadCurrentEmpresa();
            } catch {
                // Sin contribuyentes o sin conexión: el badge muestra "pendiente".
            }
        }
    }

    /** Etiqueta compacta del consorcio (nombre o fallback al ID) */
    readonly tenantLabel = computed(() => {
        const t = this.currentTenant();
        if (t?.tenantName) return t.tenantName;
        return null;
    });

    readonly empresaLabel = computed(() => {
        const e = this.currentEmpresa();
        return e?.razonSocial ?? null;
    });

    readonly sucursalLabel = computed(() => {
        const s = this.currentSucursal();
        return s?.nombre ?? null;
    });
}